import { useEffect, useState } from "react";

import { generateUUIDv4, humanizedFileSize } from "@/utils";
import {
  showAudioViewer,
  showImageViewer,
  showPdfViewer,
  showVideoViewer,
} from "@/components/molecules/alert";
import type { IFileObject } from "@/interfaces";

export type IFileViewerType = File | IFileObject;

export function getFileDataInitValue(file: IFileViewerType): IFileObject {
  /**
   * indicate prop.file is File or IFileObject
   */
  const isFile = file instanceof File;

  if (isFile)
    return {
      name: file.name,
      size: file.size,
      type: file.type,
      url: window.URL.createObjectURL(file),
      id: generateUUIDv4(),
    };

  return file;
}

/**
 *  File reader/loader & viewer API
 * @param file  The file itself or the IFileObject
 * @returns
 */
export function useFileViewer(file: IFileViewerType) {
  const [fileData, setFileData] = useState<IFileObject>();

  /**
   * indicate file type is image or not
   * @param type file type
   * @returns
   */

  const isImage = file?.type?.includes("image/") || false;
  const isVideo = file?.type?.includes("video") || false;
  const isAudio = file?.type?.includes("audio") || false;
  const isPdf = file?.type?.includes("pdf") || false;

  const canShow = isPdf || isAudio || isVideo || isImage;

  useEffect(() => {
    const _fileData = getFileDataInitValue(file);

    setFileData(_fileData);

    return () => {
      if (_fileData?.url) window.URL.revokeObjectURL(_fileData.url);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   *
   * @returns show the image in a modal
   */
  function show() {
    if (fileData) {
      if (isImage)
        showImageViewer({
          title: fileData.name,
          src: fileData.url,
          icon: "Gallery",
          date: humanizedFileSize(fileData.size),
        });
      if (isVideo)
        showVideoViewer({
          title: fileData.name,
          src: fileData.url,
          date: humanizedFileSize(fileData.size),
        });
      if (isAudio)
        showAudioViewer({
          title: fileData.name,
          src: fileData.url,
          date: humanizedFileSize(fileData.size),
        });
      if (isPdf) {
        showPdfViewer({
          title: fileData.name,
          src: fileData.url,
          date: humanizedFileSize(fileData.size),
        });
      }
    }
  }

  return {
    isImage,
    isVideo,
    isPdf,
    isAudio,
    canShow,
    fileData,
    show,
  };
}
